import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  ChevronUpIcon,
  ClipboardIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { getSourceIcon, SOURCE_ICON_MAPPINGS } from '../../config/sourceIcons';

const OpenverseRecommendations = ({ recommendations = [] }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [linksCopied, setLinksCopied] = useState(false);

  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  // Only show top 3 most relevant
  const topRecommendations = recommendations.slice(0, 3);

  // Get Openverse icon component
  const getOpenverseIcon = () => {
    const openverseMapping = SOURCE_ICON_MAPPINGS.find(mapping => 
      mapping.patterns.includes('openverse.fyi')
    );
    
    if (openverseMapping && openverseMapping.type === 'svg') {
      return openverseMapping.component('w-4 h-4', {
        primary: 'text-gray-400',
        secondary: 'text-gray-500',
        accent: 'text-gray-400',
        dot: 'text-gray-500'
      });
    }
    
    return <span className="text-gray-400">🌌</span>; // Fallback
  };

  const copyAllLinks = async () => {
    try {
      // Format recommendations as title: URL pairs
      const linksWithTitles = topRecommendations
        .filter(rec => rec.url)
        .map(rec => `${rec.title || 'Untitled'}: ${rec.url}`)
        .join('\n');
      
      if (linksWithTitles) {
        await navigator.clipboard.writeText(linksWithTitles);
        setLinksCopied(true);
        setTimeout(() => setLinksCopied(false), 2000);
      }
    } catch (error) {
      console.error('Failed to copy recommendation links to clipboard:', error);
    }
  };



  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 p-3 bg-gray-800/20 border border-gray-700/30 rounded-lg"
    >
      <div className="flex items-center justify-between p-2">
        <div className="flex items-center space-x-2">
          {getOpenverseIcon()}
          <span className="text-sm font-medium text-gray-300">Openverse Recommendations</span>
          <span className="text-xs text-gray-500 bg-gray-700/50 px-2 py-0.5 rounded">
            {topRecommendations.length}
          </span>
        </div>
        
        {/* Expand/Collapse Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-400 hover:text-white transition-colors rounded-lg p-1 hover:bg-gray-700/50"
          title={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </motion.button>
      </div>

      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 space-y-2"
          >
            {topRecommendations.map((recommendation, index) => (
              <motion.div
                key={recommendation.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-800/30 border border-gray-700/30 rounded-md p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        {getSourceIcon(recommendation, { muted: true })}
                      </div>
                      <h4 className="text-sm font-medium text-gray-200 leading-tight">
                        {recommendation.title}
                      </h4>
                    </div>
                    
                    {recommendation.metadata?.description && (
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2 leading-relaxed">
                        {recommendation.metadata.description}
                      </p>
                    )}
                  </div>
                  
                  <a
                    href={recommendation.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 text-xs text-gray-400 hover:text-gray-300 transition-colors underline"
                    title="View resource"
                  >
                    View
                  </a>
                </div>
              </motion.div>
            ))}
            
            <div className="pt-2 border-t border-gray-700/30">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  <a 
                    href="https://openverse.fyi" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-gray-400 transition-colors underline"
                  >
                    From The Openverse
                  </a>
                </p>
                
                {/* Copy Links Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={copyAllLinks}
                  className="text-xs text-gray-400 hover:text-white flex items-center transition-colors rounded-lg px-2 py-1 hover:bg-gray-700/50"
                  title="Copy all recommendation links"
                >
                  {linksCopied ? (
                    <>
                      <CheckIcon className="w-3 h-3 mr-1 text-green-400" />
                      Copied
                    </>
                  ) : (
                    <>
                      <ClipboardIcon className="w-3 h-3 mr-1" />
                      Copy Links
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default OpenverseRecommendations; 